import { TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { InputError } from 'shared/components/form/input-error';
import { SliderFilterPanel } from 'shared/components/web-filter-controls';

import {
  LocationRadiusMap,
  type LocationRadiusCoordinate,
} from 'shared/components/location-radius-map.web';
import { Text } from 'shared/components/text';
import { getMarketCenter } from 'shared/library/approximate-location';
import { useLocale } from 'shared/library/i18n/use-locale';
import { useApproximateLocation } from 'shared/library/use-approximate-location.web';
import { useLocation } from 'shared/library/use-location';
import useLocationStore from 'shared/state/location';

export type LocationFilterValue = LocationRadiusCoordinate & {
  maxDistanceInKm: number;
};

type LocationFilterPanelProps = {
  coordinate: LocationRadiusCoordinate | null;
  maxDistanceInKm: number | null;
  country: string | null;
  onChange: (value: LocationFilterValue) => void;
  onClear?: () => void;
};

export function LocationFilterPanel({
  coordinate,
  maxDistanceInKm,
  country,
  onChange,
  onClear,
}: LocationFilterPanelProps) {
  const { t } = useTranslation('components');
  const { countryCode } = useLocale();
  const { fetchPlace, errorMessage } = useLocation({ approximate: false });
  const filteredCountryCenter = getMarketCenter(country);
  const marketCenter = getMarketCenter(countryCode) ?? getMarketCenter('SE');
  const approximateLocationState = useApproximateLocation(
    coordinate ?? filteredCountryCenter,
    marketCenter,
  );
  const approximateLocation = approximateLocationState.location;
  const distance = maxDistanceInKm ?? 50;

  return (
    <View className={'min-h-0 flex-1'}>
      <View
        className={
          'bg-brand-cream-dark relative min-h-64 flex-1 overflow-hidden md:h-[430px] md:flex-initial'
        }
      >
        <LocationRadiusMap
          coordinate={coordinate}
          initialCenter={approximateLocation}
          radiusInKm={distance}
          unavailableLabel={t('listings_filter_form.map_unavailable')}
          instructionLabel={t('listings_filter_form.select_location_hint')}
          onCoordinateChange={({ latitude, longitude }) => {
            useLocationStore.getState().setErrorMessage(null);
            onChange({ latitude, longitude, maxDistanceInKm: distance });
          }}
          onLocate={(coordinate) => {
            void fetchPlace(coordinate);
            onChange({ ...coordinate, maxDistanceInKm: distance });
          }}
          className={'!h-full !rounded-none'}
        />
      </View>

      <View className={'bg-brand-cream-light px-5 py-6 md:px-8'}>
        {errorMessage && (
          <View className={'mb-6'}>
            <InputError
              title={errorMessage}
              errors={{ location: [t('listings_filter_form.choose_on_map_instead')] }}
            />
          </View>
        )}

        <SliderFilterPanel
          caption={t('listings_filter_form.max_distance')}
          valueLabel={coordinate ? `${distance} km` : t('listings_filter_form.choose_location')}
          action={
            coordinate &&
            onClear && (
              <TouchableOpacity
                accessibilityRole={'button'}
                accessibilityLabel={`${t('common:clear')} ${t('listings_filter_form.location')}`}
                className={'hover:bg-brand-cream-dark rounded px-2 py-1 transition-colors'}
                onPress={onClear}
              >
                <Text className={'text-brand-brown-dark text-sm font-bold underline'}>
                  {t('common:clear')}
                </Text>
              </TouchableOpacity>
            )
          }
          marks={[5, 50, 100, 150, 200].map((value) => ({ value, label: `${value} km` }))}
          value={distance}
          min={5}
          max={200}
          roundTo={5}
          suffix={'km'}
          accessibilityLabel={t('listings_filter_form.max_distance')}
          disabled={!coordinate}
          onChange={(nextDistance) => {
            if (coordinate && nextDistance !== null) {
              onChange({ ...coordinate, maxDistanceInKm: nextDistance });
            }
          }}
        />
      </View>
    </View>
  );
}
